import { CatMorph } from "./morph.js";
import { CatPelt } from "./pelt.js";
import { CustomizerPanel } from "./panel.js";
import { loadLast, rememberLast } from "./store.js";
import { BASES, defaultLook, isDefault, normalize } from "@catworld/shared";

/**
 * The cat builder, as one object main.js can hold.
 *
 * A look is owned here and applied to whichever cat is on screen, rather than
 * each cat owning its own: the five shipped cats are five starting points for
 * the SAME cat, so dragging "chonk" and then trying the toon base has to carry
 * the chonk across.  The morph and pelt for a base are built the first time
 * that base is worn and then kept -- rebuilding one means re-reading the rest
 * pose out of a skeleton the mixer has already moved, which would bake the
 * current animation frame in as the new rest pose.
 */
export class Customizer {
  /**
   * @param cats  the loaded Cat instances, index-aligned with main.js's CATS
   * @param mount the empty #customizer element
   * @param setBase(index)  main.js's switchCat -- visibility, HUD, clip state
   */
  constructor({ cats, mount, setBase }) {
    this.cats = cats;
    this.setBase = setBase;
    this.rigs = new Map();              // cat index -> { morph, pelt }
    this.active = -1;
    this.onName = null;                 // main.js hangs the HUD refresh here

    // A look survives a reload, so a cat someone built and did not name is
    // still theirs when they come back.  Anything unparseable normalizes to
    // the neutral look rather than throwing the game away at boot.
    //
    // Whether it was RESTORED is kept, because start() has to tell a base the
    // player chose from one this module made up.  defaultLook() names "sandy"
    // -- a value in schema.js, not a decision anybody made -- and without
    // this flag that value silently outranks the cat main.js starts on.
    const stored = loadLast();
    this.restored = stored !== null;
    this.look = normalize(stored || defaultLook());

    this.panel = new CustomizerPanel({
      mount,
      initial: this.look,
      onChange: (look) => this.set(look),
      onBase: (key) => {
        const i = BASES.indexOf(key);
        if (i >= 0) this.setBase(i);
      },
    });
  }

  /**
   * Take the wheel at boot.
   *
   * A look restored from a previous session names the base it was built on,
   * and main.js's own starting cat knows nothing about it -- landing the
   * player on Tabby while the panel reads "Sandy" is the visible half of
   * that; the invisible half is a look whose proportions were tuned against
   * a rig the player is no longer wearing.
   */
  start(fallback) {
    // Only a look the player actually left behind gets to move them off the
    // cat main.js chose.  A first-run look carries schema.js's default base,
    // which is not a preference and must not win: with the game starting on
    // Tabby, honouring it dropped every new player onto Sandy instead.
    const i = BASES.indexOf(this.look.base);
    if (this.restored && i >= 0 && i !== fallback) this.setBase(i);
    else this.setActive(fallback);
  }

  /**
   * The cat main.js is currently showing.  Applies the look to it.
   *
   * Also the one place the base is reconciled: the HUD switcher and the debug
   * number keys change cat without going through the panel, and a look whose
   * `base` disagreed with the cat on screen would save wrong and reload onto
   * the wrong rig.
   */
  setActive(i) {
    this.active = i;
    const key = BASES[i];
    if (key && this.look.base !== key) {
      this.look = { ...this.look, base: key };
      this.panel.showBase(key);           // the base alone: see panel.showBase
      rememberLast(this.look);
    }
    this.apply();
  }

  set(look) {
    const next = normalize(look);
    const renamed = this.look.name !== next.name;
    this.look = next;
    // A look names the rig it was built on, so adopting one has to be able to
    // change which cat is on screen.  Without this, loading a saved toon cat
    // put its proportions on whatever cat happened to be out -- half the look
    // silently doing nothing, which reads as the save being broken.
    const i = BASES.indexOf(next.base);
    if (i >= 0 && i !== this.active) this.setBase(i);   // ends in setActive
    else this.apply();
    rememberLast(this.look);
    // Edge-triggered: the name field fires an input event per keystroke, and
    // the HUD write is the only part of this that touches layout.
    if (renamed) this.onName?.();
    // The look also has to reach the other people in the world, who are
    // rendering this cat from a record the server holds.  Fired on every set
    // -- including the per-keystroke renames above -- because the alternative
    // is a rule about which changes are worth telling anyone, and net/ is
    // where the rate limiting belongs.  Nothing is hung here when the game is
    // running offline, so a file:// session pays one optional-call check.
    this.onLook?.(this.look);
  }

  /** Reflect a look chosen elsewhere (a saved cat, Randomise) into the panel. */
  setLook(look) {
    this.set(look);
    this.panel.setLook(this.look);
  }

  toggle() { this.panel.toggle(); }
  get isOpen() { return this.panel.isOpen; }

  /**
   * The name shown in the HUD: the player's, if they gave one.
   *
   * A blank name is not "nameless", it is "has not been renamed", so it falls
   * back to the base cat's own display name rather than to an empty line.
   */
  displayName(fallback) {
    return this.look.name.trim() || fallback;
  }

  apply() {
    const cat = this.cats[this.active];
    if (!cat) return;
    let rig = this.rigs.get(this.active);
    if (!rig) {
      // Nothing is built for a cat still wearing the neutral look: the morph
      // constructor walks the skeleton and the pelt patch forces a shader
      // recompile, and neither is worth spending on a player who never opens
      // the builder.
      if (isDefault(this.look)) return;
      rig = { morph: new CatMorph(cat.root), pelt: new CatPelt(cat.root) };
      this.rigs.set(this.active, rig);
      cat.morph = rig.morph;            // cat.update() re-applies it post-mixer
    }
    rig.morph.apply(this.look);
    rig.pelt.apply(this.look);
  }
}
